import { gkApi } from './index';

const PAGE_SIZE = 20;

const logbookApi = gkApi.injectEndpoints({
  endpoints: (builder) => ({
    // Paginated, newest-first list. When `search` is set, hits the FTS
    // endpoint. Pages are merged for infinite scroll (offset based).
    listLogEntries: builder.query({
      query: ({ eventId, search, offset = 0, limit = PAGE_SIZE } = {}) => ({
        url: search ? '/logbook/search/' : '/logbook/',
        method: 'get',
        params: {
          event: eventId,
          q: search || undefined,
          offset,
          limit,
        },
      }),
      transformResponse: (data, meta) => ({
        items: data,
        ...(meta?.envelope || {}),
      }),
      // One cache bucket per (event, search) pair; offset only changes the page.
      serializeQueryArgs: ({ queryArgs }) => ({
        eventId: queryArgs.eventId,
        search: queryArgs.search || '',
      }),
      merge: (current, incoming, { arg }) => {
        if (!arg || (arg.offset ?? 0) === 0) {
          current.items = incoming.items;
        } else {
          const seen = new Set(current.items.map((e) => e.id));
          current.items.push(...incoming.items.filter((e) => !seen.has(e.id)));
        }
        current.count = incoming.count;
        current.next = incoming.next;
        current.offset = incoming.offset;
        current.limit = incoming.limit;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.offset !== previousArg?.offset ||
        currentArg?.search !== previousArg?.search ||
        currentArg?.eventId !== previousArg?.eventId,
      providesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
    createLogEntry: builder.mutation({
      query: (payload) => ({ url: '/logbook/', method: 'post', body: payload }),
      invalidatesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
    updateLogEntry: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/logbook/${id}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
    deleteLogEntry: builder.mutation({
      query: (id) => ({ url: `/logbook/${id}/`, method: 'delete' }),
      invalidatesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
    createLogComment: builder.mutation({
      query: ({ entry, body }) => ({
        url: '/log-comment/',
        method: 'post',
        body: { entry, body },
      }),
      invalidatesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
    updateLogComment: builder.mutation({
      query: ({ id, body }) => ({
        url: `/log-comment/${id}/`,
        method: 'PATCH',
        body: { body },
      }),
      invalidatesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
    deleteLogComment: builder.mutation({
      query: (id) => ({ url: `/log-comment/${id}/`, method: 'delete' }),
      invalidatesTags: [{ type: 'LOGBOOK_LIST' }],
    }),
  }),
});

export const {
  useListLogEntriesQuery,
  useCreateLogEntryMutation,
  useUpdateLogEntryMutation,
  useDeleteLogEntryMutation,
  useCreateLogCommentMutation,
  useUpdateLogCommentMutation,
  useDeleteLogCommentMutation,
} = logbookApi;

export { PAGE_SIZE };
